import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Menu, X, ChevronDown, Compass, Settings, TrendingUp, Package, Database } from "lucide-react";

const products = [
  { name: "FundMatch", href: "/products/fundmatch", icon: Compass, description: "AI-powered grant discovery", color: "teal" },
  { name: "AdminHub", href: "/products/adminhub", icon: Settings, description: "Workflow automation", color: "amber" },
  { name: "ImpactGrow", href: "/products/impactgrow", icon: TrendingUp, description: "Impact measurement", color: "cyan" },
  { name: "ResourceHub", href: "/products/resourcehub", icon: Package, description: "Tools & partnerships", color: "violet" },
  { name: "DataIntel", href: "/products/dataintel", icon: Database, description: "Data intelligence", color: "blue" },
];

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Determine header style based on page and scroll
  const isDark = isHomePage && !isScrolled;

  return (
    <header
      data-testid="marketing-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-white/95 backdrop-blur-md shadow-sm border-b border-zinc-100"
          : isDark
          ? "bg-transparent"
          : "bg-white/95 backdrop-blur-md shadow-sm border-b border-zinc-100"
      }`}
    >
      <nav className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${isDark ? 'bg-teal-500' : 'bg-gradient-to-br from-slate-400 to-teal-500'}`}>
              <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
              </svg>
            </div>
            <span className={`font-heading font-bold text-xl ${isDark ? 'text-white' : 'text-zinc-900'}`}>
              Impact Plexus
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className={`font-medium ${isDark ? 'text-slate-300 hover:text-white hover:bg-white/10' : 'text-zinc-700 hover:text-zinc-900'}`}
                  data-testid="products-dropdown"
                >
                  Products <ChevronDown className="ml-1 h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-72 p-2 bg-slate-900 border-slate-800">
                {products.map((product) => {
                  const colorClasses = {
                    teal: "text-teal-400",
                    amber: "text-amber-400",
                    cyan: "text-cyan-400",
                    violet: "text-violet-400",
                    blue: "text-blue-400",
                  };
                  return (
                    <DropdownMenuItem key={product.name} asChild>
                      <Link
                        to={product.href}
                        className="flex items-start gap-3 p-3 rounded-lg hover:bg-slate-800 cursor-pointer"
                        data-testid={`nav-${product.name.toLowerCase()}`}
                      >
                        <product.icon className={`h-5 w-5 ${colorClasses[product.color]} mt-0.5`} />
                        <div>
                          <div className="font-medium text-white">{product.name}</div>
                          <div className="text-sm text-slate-400">{product.description}</div>
                        </div>
                      </Link>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>

            <Link to="/#how-it-works">
              <Button
                variant="ghost"
                className={`font-medium ${isDark ? 'text-slate-300 hover:text-white hover:bg-white/10' : 'text-zinc-700 hover:text-zinc-900'}`}
              >
                How it Works
              </Button>
            </Link>
            <Link to="/pricing">
              <Button
                variant="ghost"
                className={`font-medium ${isDark ? 'text-slate-300 hover:text-white hover:bg-white/10' : 'text-zinc-700 hover:text-zinc-900'}`}
              >
                Pricing
              </Button>
            </Link>
          </div>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center gap-3">
            <Link to="/dashboard">
              <Button
                variant="ghost"
                className={`font-medium ${isDark ? 'text-slate-300 hover:text-white hover:bg-white/10' : 'text-zinc-700 hover:text-zinc-900'}`}
                data-testid="signin-btn"
              >
                Dashboard
              </Button>
            </Link>
            <Link to="/dashboard">
              <Button
                className="bg-teal-500 hover:bg-teal-600 text-white font-medium px-5"
                data-testid="get-started-btn"
              >
                Get Started
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <Button
            variant="ghost"
            size="icon"
            className={`md:hidden ${isDark ? 'text-white hover:bg-white/10' : ''}`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden border-t border-slate-800 bg-slate-900"
            >
              <div className="py-4 space-y-2">
                {products.map((product) => {
                  const colorClasses = {
                    teal: "text-teal-400",
                    amber: "text-amber-400",
                    cyan: "text-cyan-400",
                    violet: "text-violet-400",
                    blue: "text-blue-400",
                  };
                  return (
                    <Link
                      key={product.name}
                      to={product.href}
                      className="flex items-center gap-3 px-4 py-3 hover:bg-slate-800 rounded-lg"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <product.icon className={`h-5 w-5 ${colorClasses[product.color]}`} />
                      <span className="font-medium text-white">{product.name}</span>
                    </Link>
                  );
                })}
                <div className="pt-4 px-4 space-y-2 border-t border-slate-800">
                  <Link to="/dashboard" onClick={() => setMobileMenuOpen(false)}>
                    <Button className="w-full bg-teal-500 hover:bg-teal-600 text-white">
                      Get Started
                    </Button>
                  </Link>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </header>
  );
};

const Footer = () => {
  return (
    <footer className="bg-slate-900 text-white" data-testid="marketing-footer">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl py-16">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-8 mb-12">
          {/* Brand */}
          <div className="col-span-2">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-9 h-9 rounded-lg bg-teal-500 flex items-center justify-center">
                <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                </svg>
              </div>
              <span className="font-heading font-bold text-xl">Impact Plexus</span>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed max-w-xs">
              AI-powered grant intelligence for Canadian nonprofits. Discover funding, automate admin, and grow your impact.
            </p>
          </div>

          {/* Products */}
          <div>
            <h4 className="font-heading font-semibold mb-4 text-sm uppercase tracking-wider text-slate-400">Products</h4>
            <ul className="space-y-3">
              {products.map((product) => (
                <li key={product.name}>
                  <Link to={product.href} className="text-slate-300 hover:text-white transition-colors text-sm">
                    {product.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h4 className="font-heading font-semibold mb-4 text-sm uppercase tracking-wider text-slate-400">Resources</h4>
            <ul className="space-y-3">
              <li><a href="#" className="text-slate-300 hover:text-white transition-colors text-sm">Documentation</a></li>
              <li><a href="#" className="text-slate-300 hover:text-white transition-colors text-sm">API Reference</a></li>
              <li><a href="#" className="text-slate-300 hover:text-white transition-colors text-sm">Grant Directory</a></li>
              <li><a href="#" className="text-slate-300 hover:text-white transition-colors text-sm">Blog</a></li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="font-heading font-semibold mb-4 text-sm uppercase tracking-wider text-slate-400">Company</h4>
            <ul className="space-y-3">
              <li><Link to="/about" className="text-slate-300 hover:text-white transition-colors text-sm">About</Link></li>
              <li><a href="#" className="text-slate-300 hover:text-white transition-colors text-sm">Careers</a></li>
              <li><a href="#" className="text-slate-300 hover:text-white transition-colors text-sm">Contact</a></li>
              <li><a href="#" className="text-slate-300 hover:text-white transition-colors text-sm">Privacy</a></li>
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="pt-8 border-t border-slate-800 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-slate-500 text-sm">
            © 2026 Impact Plexus. Empowering 83,000+ Canadian nonprofits.
          </p>
          <div className="flex items-center gap-6">
            <a href="#" className="text-slate-400 hover:text-white transition-colors text-sm">Terms</a>
            <a href="#" className="text-slate-400 hover:text-white transition-colors text-sm">Privacy</a>
            <a href="#" className="text-slate-400 hover:text-white transition-colors text-sm">Cookies</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export const MarketingLayout = ({ children }) => {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
};

export default MarketingLayout;
